import {
    ActionSpinOptions,
    ActionSpinSound,
    ASWinData, IResponseData,
    PromoPanelGameConfiguration,
} from '@nolimitcity/promo-panel/bin/interfaces/ASInterfaces';
import {ASBonusPickOptions, ASMainGamePickOptions, WINTYPE} from '@nolimitcity/promo-panel/bin/enums/ASEnums';
import {Beheaded} from './Beheaded';
import {BeheadedGameMode} from './server/data/BeheadedGameMode';
import {MathHelper} from '../core/utils/MathHelper';
import {BonusFeatureData} from '@nolimitcity/slot-launcher/bin/plugins/apiplugin/BonusFeatures';
import {Helper} from '@nolimitcity/promo-panel/bin/utils/Helper';
import {BeheadedSound} from './sound/BeheadedSound';
import {BaseServerData} from "./server/base/IData";
import {BaseModel} from "./server/base/BaseModel";

export class BeheadedPromoPanelConfig implements PromoPanelGameConfiguration {

    constructor() {
    }

    public getActionSpinOptions(): ActionSpinOptions {
        return {
            hasActionSpin: true,
            gameName: 'Beheaded',
            graphics: {
                backgroundTextureName: 'introBg', //Loaded in intro view config.
            },
            bonusGame: {
                header: () => Beheaded.apiPlugin.translations.translate("BONUS TZANTZA SPINS"),
                options: () => [
                    {name: "", id: 1, image: Beheaded.apiPlugin.translations.language==="us-sweepstakes"?"butterfly_US_Sweepstakes.png":"butterfly.png"},
                    {name: "", id: 2, image: "skull.png"},
                ],
                type: ASBonusPickOptions.PICK_MODE,
                pickNeededForModes: [BeheadedGameMode.SUPER_FREESPIN_END, BeheadedGameMode.SUPER_FREESPIN_AVALANCHE, BeheadedGameMode.FREESPIN, BeheadedGameMode.FREESPIN_AVALANCHE],
                pickNeededForBuyFeatures: ["SUPER_FREESPIN", "LUCKY_DRAW", "FREESPIN"] //Needs to be the actual feature name used by server.
            },
            getParsedData: (response: BaseServerData, bet: number, prevResponse: BaseServerData): ASWinData => {

                const spinWin = this.getSpinWin(response, bet, prevResponse);

                const parsedData: ASWinData = {
                    totalWin: response.accumulatedRoundWin,
                    wasFeatureBuy: response.wasFeatureBuy,
                    freeSpinTriggeredThisSpin: this.isFreespinTriggeredThisSpins(response),
                    totalSpinWinnings: spinWin,
                    isBigWin: this.isBigWin(response, spinWin),
                    winType: WINTYPE.NORMAL,
                    featureName: this.getFeatureName(response),
                    isRoundComplete: this.isRoundComplete(response),
                    isBonusEnd: this.isBonusEnd(response),
                    isWinCapHit: false,
                    waitForAnimation: this.isLevelUp(response,this.isBigWin(response, spinWin)),
                
                };
                
                return parsedData;
            },
        };
    }

    public isLevelUp(response: BaseServerData,isBigWin:boolean): boolean {
        return response.currentLevel < response.nextLevel || isBigWin;
    }

    public onActionSpinPlaySound(trigger: ActionSpinSound): void {
        if (trigger == ActionSpinSound.WIN) {
            BeheadedSound.soundPlayer.playTrack({name: BeheadedSound.AS_WIN});
        } else if (trigger == ActionSpinSound.BIG_WIN) {
            BeheadedSound.soundPlayer.playTrack({name: BeheadedSound.AS_WIN_BIG});
        }
    }

    private getSpinWin(response: BaseServerData, bet: number, prevData: BaseServerData): number {
        let win = response ? response.totalSpinWinnings : 0;
        win = win > 0 ? win : 0; //if negative do 0
        return win;
    }

    private isFreespinTriggeredThisSpins(response: BaseServerData) {
        if(
            [BeheadedGameMode.FREESPIN].find((v) => v == response.mode) &&
            [BeheadedGameMode.FREESPIN_AVALANCHE].find((v) => v == response.nextMode) &&
            response.enhancedBet && response.currentLevel == 1
        ){

            /*
                HACK - Overwriting mode with BeheadedGameMode.NORMAL
                This avoids the conflict with the pick state and fs trigger. Normally you have one spin will trigger a mode where you do picks.
                The special circumstance for this game is that FREESPIN isn't really a pick mode, but you need to make the choice before starting FS (because we can't add a pick choice in the middle of FS).
             */
            response.mode = BeheadedGameMode.NORMAL;
            return true;
        }

        if(
            [BeheadedGameMode.NORMAL,BeheadedGameMode.NORMAL_AVALANCHE].find((v) => v == response.mode) &&
            [BeheadedGameMode.SUPER_FREESPIN,BeheadedGameMode.SUPER_FREESPIN_AVALANCHE].find((v) => v == response.nextMode)
        ) return true;
           
        if(
            [BeheadedGameMode.NORMAL,BeheadedGameMode.NORMAL_AVALANCHE].find((v) => v == response.mode) &&
            [BeheadedGameMode.FREESPIN,BeheadedGameMode.FREESPIN_AVALANCHE].find((v) => v == response.nextMode)
        ) return true;

        return false;
    }

    private isBigWin(response: BaseServerData, spinWin: number): boolean {
        let win = spinWin;

        if (response.wasFeatureBuy) {
            const betPrice = this.getBuyFeaturePrice(response);
            let winRatio: number = Helper.ceilToDecimals(win / betPrice, 2);
            return winRatio >= 1;
        }

        let winRatio: number = Helper.ceilToDecimals(win / response.playedBetValue, 2);
        return winRatio >= 15;
    }

    private getBuyFeaturePrice(response: BaseServerData): number {
        let betLevel = Beheaded.apiPlugin.freeBets.hasFreeBets()
            ? Beheaded.apiPlugin.freeBets.getBet()
            : Beheaded.apiPlugin.betLevel.getLevel();
        let price: number = Beheaded.model.gameData?.raw.playedBetValue || parseFloat(betLevel);
        const featureData: BonusFeatureData | undefined = Beheaded.apiPlugin.bonusFeatures.getFeatureDataByName('BOOSTED_BET');
        if (response.wasFeatureBuy) {
            for (let index = 0; index < Beheaded.model.initData.featureBuyTimesBetValue.length; index++) {
                if (Beheaded.model.initData.featureBuyTimesBetValue[index].name === BaseModel.buyFeatureName) {
                    price *= Beheaded.model.initData.featureBuyTimesBetValue[index].price;
                    break;
                }
            }
        }
        return price;
    }

    private getFeatureName(response: BaseServerData): string {
        if (
            response.mode == BeheadedGameMode.FREESPIN ||
            response.nextMode == BeheadedGameMode.FREESPIN ||
            response.mode == BeheadedGameMode.FREESPIN_AVALANCHE ||
            response.nextMode == BeheadedGameMode.FREESPIN_AVALANCHE
        ) {
            let isFirstLevel = !response.mode.includes("FREESPIN");
            return 'AYAHUASCA SPINS ' + Beheaded.apiPlugin.translations.translate('Level') + ' ' + (isFirstLevel ? response.nextLevel : response.currentLevel);
        }

        if (
            response.mode == BeheadedGameMode.FREESPIN_SUPER ||
            response.nextMode == BeheadedGameMode.FREESPIN_SUPER ||
            response.mode == BeheadedGameMode.SUPER_FREESPIN ||
            response.nextMode == BeheadedGameMode.SUPER_FREESPIN ||
            response.mode == BeheadedGameMode.SUPER_FREESPIN_AVALANCHE ||
            response.nextMode == BeheadedGameMode.SUPER_FREESPIN_AVALANCHE
        ) {
            return 'TZANTZA SPINS';
        }

        if (response.mode == BeheadedGameMode.SUPER_FREESPIN_END ||
            response.nextMode == BeheadedGameMode.SUPER_FREESPIN_END
        ){
            let chioce = "BUTTERFLY BOTTLE";

            if (response.playerSelect == 2){
                chioce = "SKULL BOTTLE";
            }

            return 'TZANTZA SPINS - ' + chioce;
        }

        return response.mode;
    }

    private isRoundComplete(response: BaseServerData): boolean {

        return (
            !![BeheadedGameMode.FREESPIN, BeheadedGameMode.SUPER_FREESPIN, BeheadedGameMode.NORMAL, BeheadedGameMode.SUPER_FREESPIN_END].find((v ) => v === response.nextMode) 
        ) || this.isFreespinTriggeredThisSpins(response);
    }

    private isBonusEnd(response: BaseServerData): boolean {
        return (
            response.nextMode === BeheadedGameMode.NORMAL &&
            (response.mode === BeheadedGameMode.FREESPIN || response.mode === BeheadedGameMode.SUPER_FREESPIN_END)
        );
    }
}
